import {addMonths} from 'date-fns';
import {Alert, Attributes, AttributesRow} from 'platform/components';
import {HStack, Spinner, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {GetCarPassRequestResponseBody} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER} from '@dms/shared';

import {parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CarPassState} from '../utils/deriveCarPassState';

const CAR_PASS_VALIDITY_MONTHS = 6;

type PendingState =
  | typeof CarPassState.PENDING
  | typeof CarPassState.WAITING_FOR_DIV
  | typeof CarPassState.ACTION_TAKEN;

const PENDING_ALERT_KEYS: Record<PendingState, {titleKey: string; messageKey: string}> = {
  [CarPassState.PENDING]: {
    titleKey: 'page.carPass.labels.pendingAlertTitle',
    messageKey: 'page.carPass.labels.pendingAlertMessage',
  },
  [CarPassState.WAITING_FOR_DIV]: {
    titleKey: 'page.carPass.labels.waitingForDivAlertTitle',
    messageKey: 'page.carPass.labels.waitingForDivAlertMessage',
  },
  [CarPassState.ACTION_TAKEN]: {
    titleKey: 'page.carPass.labels.actionTakenAlertTitle',
    messageKey: 'page.carPass.labels.actionTakenAlertMessage',
  },
};

export interface CarPassBodyProps extends RequiredTestIdProps {
  uiState: Exclude<CarPassState, typeof CarPassState.NONE>;
  request: GetCarPassRequestResponseBody | undefined;
}

export function CarPassBody(props: CarPassBodyProps) {
  const formatDateTime = useDateTimeFormatter();

  const readyAtDate = isNotNil(props.request?.readyAt) ? parseDate(props.request.readyAt) : null;
  const publishDateLabel = isNotNil(readyAtDate)
    ? formatDateTime('dateShort', readyAtDate)
    : EMPTY_PLACEHOLDER;
  const validUntilLabel = isNotNil(readyAtDate)
    ? formatDateTime('dateShort', addMonths(readyAtDate, CAR_PASS_VALIDITY_MONTHS))
    : EMPTY_PLACEHOLDER;
  const readyRows: AttributesRow[] = [
    {label: i18n.t('page.carPass.labels.publishedOn'), value: publishDateLabel},
    {label: i18n.t('page.carPass.labels.validUntil'), value: validUntilLabel},
  ];

  const expiredDateLabel = isNotNil(props.request?.expiredAt)
    ? formatDateTime('dateShort', parseDate(props.request.expiredAt))
    : undefined;
  const expiredMessage = isNotNil(expiredDateLabel)
    ? i18n.t('page.carPass.labels.expiredAlertMessageWithDate', {date: expiredDateLabel})
    : i18n.t('page.carPass.labels.expiredAlertMessage');

  return match(props.uiState)
    .with(
      CarPassState.PENDING,
      CarPassState.WAITING_FOR_DIV,
      CarPassState.ACTION_TAKEN,
      (state) => (
        <VStack spacing={3}>
          <Alert
            type="banner"
            variant="info"
            title={i18n.t(PENDING_ALERT_KEYS[state].titleKey)}
            message={i18n.t(PENDING_ALERT_KEYS[state].messageKey)}
          />
          <HStack spacing={2} align="center">
            <Spinner size="small" />
            <Text size="small" color="secondary">
              {i18n.t('page.carPass.labels.pendingWaiting')}
            </Text>
          </HStack>
        </VStack>
      )
    )
    .with(CarPassState.ACTION_REQUIRED, () => (
      <Alert
        type="banner"
        variant="error"
        title={i18n.t('page.carPass.labels.actionRequiredAlertTitle')}
        message={i18n.t('page.carPass.labels.actionRequiredAlertMessage')}
      />
    ))
    .with(CarPassState.READY, () => (
      <Attributes rows={readyRows} data-testid={suffixTestId('ready', props)} />
    ))
    .with(CarPassState.EXPIRED, () => (
      <Alert
        type="banner"
        variant="warning"
        title={i18n.t('page.carPass.labels.expiredAlertTitle')}
        message={expiredMessage}
      />
    ))
    .with(CarPassState.FAILED, () => (
      <Alert
        type="banner"
        variant="error"
        title={i18n.t('page.carPass.labels.errorAlertTitle')}
        message={i18n.t('page.carPass.labels.errorAlertMessage')}
      />
    ))
    .exhaustive();
}
